import React, { useState, useEffect } from "react";

import { getRequest, postRequest } from "../../services/api/apiService";
import ConnectedPlatformCard from "./components/ConnectedPlatform/ConnectedPlatformCard";
import ConnectedPlatformModal from "./components/ConnectedPlatform/modals/ConnectedPlatformModal";
import ConnectedPlatformApiConfigurationModal from "./components/ConnectedPlatform/modals/ConnectedPlatformApiConfigurationModal";
import ConnectedPlatformAccountConfigurationModal from "./components/ConnectedPlatform/modals/ConnectedPlatformAccountConfiguration/ConnectedPlatformAccountConfigurationModal";
import ConnectedPlatformTaboolaConnectModal from "./components/ConnectedPlatform/modals/ConnectedPlatformTaboolaConnectModal";

import {
  allAdConfigurations,
  platformDetailsData,
  isOAuthFlow,
} from "../../helpers/adsConfigurations";
import { headers } from "utils/common";

const ConnectedPlatforms = () => {
  // const [currentView, setCurrentView] = useState("list");
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [adsConfiguration, setAdsConfiguration] = useState(null);
  const [loading, setLoading] = useState(false); // Loading state

  // Api Configuration Modal states
  const [isApiConfigurationModalOpen, setIsApiConfigurationModalOpen] =
    useState(false);

  const [isTaboolaConnectModalOpen, setIsTaboolaConnectModalOpen] = useState(false);

  const openApiConfigurationModal = () => {
    setIsApiConfigurationModalOpen(true);
    setIsModalOpen(false);
  };

  const closApiConfigurationeModal = () =>
    setIsApiConfigurationModalOpen(false);

  // Account Configuration Modal states
  const [isAccountConfigurationModalOpen, setIsAccountConfigurationModalOpen] =
    useState(false);

  const closAccountConfigurationeModal = () =>
    setIsAccountConfigurationModalOpen(false);

  const handleConfigurePlatformClick = (platform) => {
    setIsAccountConfigurationModalOpen(true);
    setSelectedPlatform(platform);
  };

  // const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [platforms, setPlatforms] = useState(platformDetailsData);

  const handlePlatformClick = (platform) => {
    // Taboola uses direct API credentials — skip the OAuth modal
    if (platform === allAdConfigurations.taboolaAdsConfiguration.slug) {
      setIsTaboolaConnectModalOpen(true);
      setSelectedPlatform(platform);
      return;
    }

    switch (platform) {
      case allAdConfigurations.googleAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.googleAdsConfiguration);
        break;
      case allAdConfigurations.metaAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.metaAdsConfiguration);
        break;
      case allAdConfigurations.shopifyAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.shopifyAdsConfiguration);
        break;
      case allAdConfigurations.tiktokAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.tiktokAdsConfiguration);
        break;
      case allAdConfigurations.snapchatAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.snapchatAdsConfiguration);
        break;
      case allAdConfigurations.linkedinAdsConfiguration.slug:
        setAdsConfiguration(allAdConfigurations.linkedinAdsConfiguration);
        break;
      case allAdConfigurations.shopifyOAuthAdsConfiguration.slug: // Add Shopify OAuth Ads case
        setAdsConfiguration(allAdConfigurations.shopifyOAuthAdsConfiguration);
        break;
      default:
        setAdsConfiguration(null);
    }

    setIsModalOpen(true);
    setSelectedPlatform(platform);
  };

  // //////////// Disconnect Platform //////////// //
  const disconnectPlatform = async (platformName) => {
    try {
      const disconnectPlatformResult = await postRequest(
        "/api/connected-platforms/disconnect",
        { platform: platformName },
        { ...headers }
      );

      if (
        disconnectPlatformResult &&
        disconnectPlatformResult.success === true
      ) {
        const updatedPlatforms = platforms.map((platform) => {
          if (platform.slug === platformName) {
            return { ...platform, isConnected: false };
          } else {
            return platform;
          }
        });

        setPlatforms(updatedPlatforms);
      }
    } catch (err) {
      console.error(`Error disconnecting platform: ${platformName}`, err);
      setLoading(false);
    }
  };

  const handlePlatformDisconnectClick = async (platform) => {
    setLoading(true);
    await disconnectPlatform(platform);
    setLoading(false);
  };

  // Fetch platforms on load
  const fetchConnectedPlatforms = async () => {
    try {
      const connectedPlatformsData = await getRequest(
        "/api/connected-platforms/all",
        {},
        { ...headers }
      );

      const updatedPlatforms = platforms.map((platform) => {
        const isConnectedPlatform = connectedPlatformsData.some(
          (connectedPlatform) => connectedPlatform.name === platform.slug
        );
        return { ...platform, isConnected: isConnectedPlatform };
      });

      setPlatforms(updatedPlatforms);

      setLoading(false); // Set loading to false after fetching
    } catch (err) {
      console.error("Error fetching platforms", err);
      setLoading(false);
    }
  };

  // //////////// Fetch Connected Platform //////////// //
  useEffect(() => {
    fetchConnectedPlatforms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(384px,1fr))] gap-4">
        {platforms.map((platform, index) => (
          <ConnectedPlatformCard
            platform={platform}
            loading={loading}
            key={index}
            handlePlatformConnect={handlePlatformClick}
            handlePlatformDisconnect={handlePlatformDisconnectClick}
            handleConfigurePlatformClick={handleConfigurePlatformClick}
          ></ConnectedPlatformCard>
        ))}
      </div>

      <div className="p-6">
        {/* <button
          className="px-6 py-2 text-white bg-[#221C51] rounded-lg"
          onCl ick={() => setIsModalOpen(true)}
        >
          Open Modal
        </button> */}
        <ConnectedPlatformModal
          adsConfiguration={adsConfiguration}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          isOAuth={isOAuthFlow(selectedPlatform)}
          handleOpenApiConfigurationModal={openApiConfigurationModal}
        />

        <ConnectedPlatformApiConfigurationModal
          platformName={selectedPlatform}
          headers={headers}
          isOpen={isApiConfigurationModalOpen}
          onClose={closApiConfigurationeModal}
          handleFetchConnectedPlatforms={() => fetchConnectedPlatforms()}
        />

        <ConnectedPlatformAccountConfigurationModal
          platformName={selectedPlatform}
          headers={headers}
          isOpen={isAccountConfigurationModalOpen}
          onClose={closAccountConfigurationeModal}
          handleFetchConnectedPlatforms={() => fetchConnectedPlatforms()}
        />

        <ConnectedPlatformTaboolaConnectModal
          isOpen={isTaboolaConnectModalOpen}
          onClose={() => setIsTaboolaConnectModalOpen(false)}
          handleFetchConnectedPlatforms={() => fetchConnectedPlatforms()}
        />
      </div>
    </div>
  );
};

export default ConnectedPlatforms;
